import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { PageTransition } from '@/components/PageTransition';
import { toast } from 'sonner';
import {
  ChevronLeft,
  AlertCircle,
  AlertTriangle,
  Users,
  FileText,
  ClipboardList,
  LayoutGrid,
  Building2
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';

// Import components
import BookingHeader from './components/BookingHeader';
import OverviewContent from './details/OverviewContent';
import DelegatesContent from './details/DelegatesContent';
import DocumentsContent from './details/DocumentsContent';
import QuoteOptionsContent from './details/QuoteOptionsContent';
import TrainingProvidersContent from './details/TrainingProvidersContent';

const ViewBookingPage = () => {
  const navigate = useNavigate();
  const { bookingId, projectId, stackId } = useParams();
  const { user, company } = useAuth();
  usePageTitle('Booking Details');

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [space, setSpace] = useState(null);
  const [stack, setStack] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Booking detail state
  const [bookedOption, setBookedOption] = useState(null);
  const [trainingProvider, setTrainingProvider] = useState(null);
  const [delegateCount, setDelegateCount] = useState(0);

  // Save state (lifted from child components for header display)
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Validation state
  const [missingProviders, setMissingProviders] = useState(false);

  useEffect(() => {
    if (company?.id) {
      fetchBookingDetails();
    }
    if (projectId) {
      fetchSpaceDetails();
    }
    if (stackId) {
      fetchStackDetails();
    }
  }, [bookingId, projectId, stackId, company?.id]);

  // Fetch booked option and provider for booked status
  useEffect(() => {
    if (booking?.id && booking?.status === 'booked') {
      fetchBookedOption();
    }
  }, [booking?.id, booking?.status]);

  // Fetch delegate count
  useEffect(() => {
    if (booking?.id) {
      fetchDelegateCount();
    }
  }, [booking?.id]);

  // Update validation state when booking changes
  useEffect(() => {
    if (booking?.status === 'draft') {
      setMissingProviders(!booking?.selected_trainers || booking.selected_trainers.length === 0);
    } else {
      setMissingProviders(false);
    }
  }, [booking?.status, booking?.selected_trainers]);

  const fetchSpaceDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Error fetching space:', error);
      } else {
        setSpace(data);

        if (data && data.tools) {
          const toolsObj = typeof data.tools === 'string' ? JSON.parse(data.tools) : data.tools;
          const tools = Object.entries(toolsObj)
            .filter(([key, enabled]) => enabled === true)
            .map(([toolKey]) => ({
              id: toolKey,
              tool_type: toolKey,
              name: toolConfigs[toolKey]?.name || toolKey,
              icon: toolConfigs[toolKey]?.icon,
              color: toolConfigs[toolKey]?.color
            }));
          setSpaceTools(tools);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStackDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('project_stacks')
        .select('*')
        .eq('id', stackId)
        .single();

      if (error) {
        console.error('Error fetching stack:', error);
      } else {
        setStack(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBookingDetails = useCallback(async (silent = false) => {
    if (!company?.id) {
      if (!silent) setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);

      const { data, error } = await supabase
        .from('training_quotes')
        .select(`
          *,
          user_profiles!training_quotes_requested_by_fkey (
            id,
            display_name,
            email,
            avatar_url,
            first_name,
            last_name,
            phone_number
          ),
          projects (
            id,
            name
          ),
          companies (
            id,
            name,
            location
          )
        `)
        .eq('id', bookingId)
        .eq('company_id', company.id)
        .single();

      if (error) {
        console.error('Security: Booking not found or access denied');
        if (!silent) {
          toast.error('Booking not found or you do not have permission to view it');
          navigate('/bookings');
        }
        return;
      }

      setBooking(data);
      if (!silent) setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      if (!silent) {
        toast.error('Failed to load booking details');
        setLoading(false);
      }
    }
  }, [bookingId, company?.id, navigate]);

  const fetchBookedOption = async () => {
    try {
      const { data: option, error: optionError } = await supabase
        .from('quote_options')
        .select('*')
        .eq('training_quote_id', booking.id)
        .eq('status', 203)
        .single();

      if (optionError) {
        console.error('Error fetching booked option:', optionError);
        return;
      }

      setBookedOption(option);

      if (option?.training_provider_id) {
        const { data: trainer, error: trainerError } = await supabase
          .from('trainers')
          .select('id, trainer_name, booking_email')
          .eq('id', option.training_provider_id)
          .single();

        if (!trainerError && trainer) {
          setTrainingProvider(trainer);
        }
      } else {
        setTrainingProvider({ trainer_name: 'BOHSA', booking_email: null });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchDelegateCount = async () => {
    const { count, error } = await supabase
      .from('booking_delegates')
      .select('*', { count: 'exact', head: true })
      .eq('training_quote_id', booking.id);
    if (!error) setDelegateCount(count || 0);
  };

  // Callback for child components to report save state
  const handleSaveStateChange = useCallback((isSaving, savedAt = null, validationUpdate = null) => {
    setSaving(isSaving);
    if (savedAt) setLastSaved(savedAt);
    // Update validation state if provided
    if (validationUpdate !== null && typeof validationUpdate.hasProviders === 'boolean') {
      setMissingProviders(!validationUpdate.hasProviders);
    }
  }, []);

  // Handle submit request (draft -> live)
  const handleSubmitRequest = async () => {
    if (!booking || booking.status !== 'draft') return;

    // Validate required fields
    if (!booking.course_name?.trim()) {
      toast.error('Please enter a course name before submitting');
      return;
    }

    setSubmitting(true);
    try {
      // Fetch full quote details for submission (gets latest saved state from DB)
      const { data: quote, error: fetchError } = await supabase
        .from('training_quotes')
        .select(`
          *,
          user_profiles!training_quotes_requested_by_fkey (
            id,
            first_name,
            last_name,
            email,
            phone_number
          ),
          companies!company_id (
            id,
            name,
            location
          )
        `)
        .eq('id', booking.id)
        .single();

      if (fetchError || !quote) {
        throw new Error('Failed to fetch quote details');
      }

      // Validate trainers from the fresh DB data (not stale booking prop)
      if (!quote.selected_trainers || quote.selected_trainers.length === 0) {
        toast.error('Please select at least one training provider');
        setActiveTab('providers');
        setSubmitting(false);
        return;
      }

      // Check if any global (verified) trainer is selected
      const hasGlobalTrainer = quote.selected_trainers?.some(t => t.type === 'global');

      // Update status to live
      await supabase
        .from('training_quotes')
        .update({ status: 'live' })
        .eq('id', booking.id);

      // If global trainer selected, send to CRM
      if (hasGlobalTrainer) {
        // Build additional details HTML
        let additionalDetails = '';

        if (quote.delivery_methods && quote.delivery_methods.length > 0) {
          additionalDetails += `<p><strong>Delivery:</strong> ${quote.delivery_methods.join(', ')}</p>`;
        }

        const companyName = quote.companies?.name || 'Unknown Company';
        const bookerName = quote.user_profiles
          ? `${quote.user_profiles.first_name || ''} ${quote.user_profiles.last_name || ''}`.trim()
          : 'Unknown Booker';

        additionalDetails += `<p><strong>Company:</strong> ${companyName}</p>`;
        additionalDetails += `<p><strong>Requested by:</strong> ${bookerName}</p>`;

        if (quote.delegate_names && quote.delegate_names.length > 0) {
          additionalDetails += `<p><strong>Delegates:</strong> ${quote.delegate_names.join(', ')} (${quote.delegates || quote.delegate_names.length} total)</p>`;
        }

        if (quote.further_info) {
          additionalDetails += `<p><strong>Additional Notes:</strong></p><p>${quote.further_info}</p>`;
        }

        // Build the CRM payload
        const crmPayload = {
          requested_date: quote.requested_date,
          course_name: quote.course_name,
          delegates: quote.delegates,
          date_prefs: quote.date_prefs,
          locations: quote.locations,
          delivery_methods: quote.delivery_methods,
          additional_details: additionalDetails
        };

        // Send to CRM via Edge Function
        const { data: responseData, error: crmError } = await supabase.functions.invoke('request-training-quote', {
          body: crmPayload
        });

        if (crmError) {
          console.error('Edge function error:', crmError);
          // Revert status to draft on error
          await supabase
            .from('training_quotes')
            .update({ status: 'draft' })
            .eq('id', booking.id);
          throw new Error('Failed to send quote request');
        }

        // Extract quote data from response
        const quoteData = responseData.data?.[0] || responseData;

        // Update quote with CRM response
        await supabase
          .from('training_quotes')
          .update({
            external_quote_id: quoteData.id || quoteData.external_quote_id,
            access_token: quoteData.access_token,
            crm_status: quoteData.status || 187,
            cost: quoteData.cost,
            profit: quoteData.profit,
            margin: quoteData.margin,
            sent_date: quoteData.sent_date,
            webhook_response: responseData
          })
          .eq('id', booking.id);
      }

      // Update selected_trainers to mark them as notified
      if (quote.selected_trainers && quote.selected_trainers.length > 0) {
        const notificationTimestamp = new Date().toISOString();
        const updatedTrainers = quote.selected_trainers.map(trainer => ({
          ...trainer,
          wasNotified: true,
          notifiedAt: notificationTimestamp
        }));

        await supabase
          .from('training_quotes')
          .update({ selected_trainers: updatedTrainers })
          .eq('id', booking.id);
      }

      toast.success('Quote request submitted successfully');

      // Switch to the Quote Options tab (providers tab no longer exists in live status)
      setActiveTab('options');

      // Refresh booking details (status will now be 'live')
      fetchBookingDetails();

    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error(error.message || 'Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackClick = () => {
    if (projectId) {
      navigate(`/space/${projectId}/bookings`);
    } else if (stackId) {
      navigate(`/stack/${stackId}/bookings`);
    } else {
      navigate('/bookings');
    }
  };

  const getShortenedId = () => {
    if (!bookingId) return '';
    return bookingId.split('-')[0];
  };

  const getToolName = () => {
    if (!booking) return 'Loading...';
    const shortId = getShortenedId();
    return `#${shortId} - ${booking.course_name}`;
  };

  const getParentBreadcrumb = () => {
    if (projectId) {
      return {
        name: 'Bookings',
        onClick: () => navigate(`/space/${projectId}/bookings`)
      };
    } else if (stackId) {
      return {
        name: 'Bookings',
        onClick: () => navigate(`/stack/${stackId}/bookings`)
      };
    }
    return null;
  };

  // Get tabs based on booking status
  const getTabs = () => {
    const baseTabs = [
      { value: 'overview', label: 'Overview', icon: LayoutGrid },
      { value: 'delegates', label: 'Delegates', icon: Users },
    ];

    switch (booking?.status) {
      case 'draft':
        return [
          ...baseTabs,
          { value: 'providers', label: 'Providers', icon: Building2 },
          { value: 'documents', label: 'Documents', icon: FileText }
        ];
      case 'live':
        return [
          ...baseTabs,
          { value: 'options', label: 'Quote Options', icon: ClipboardList },
          { value: 'documents', label: 'Documents', icon: FileText }
        ];
      case 'booked':
        return [
          ...baseTabs,
          { value: 'documents', label: 'Documents', icon: FileText }
        ];
      default:
        return baseTabs;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner loading={loading} size={20} />
      </div>
    );
  }

  if (!booking) {
    return (
      <AppShell3 pageTitle="" pageDescription="">
        <div className="max-w-7xl mx-auto mt-8">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Booking Not Found</h2>
            <p className="text-gray-600 mb-6">The booking you're looking for doesn't exist or you don't have permission to view it.</p>
            <Button onClick={handleBackClick}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Bookings
            </Button>
          </Card>
        </div>
      </AppShell3>
    );
  }

  const tabs = getTabs();

  return (
    <AppShell3 pageTitle="" pageDescription="" backgroundColor="#f8f7f3">
      {/* ToolHeader for space bookings */}
      {projectId && space && (
        <ToolHeader
          spaceName={space?.name || 'Space'}
          toolName={getToolName()}
          currentTool="bookings"
          spaceTools={spaceTools}
          parentBreadcrumb={getParentBreadcrumb()}
        />
      )}

      {/* ToolHeader for stack bookings */}
      {stackId && stack && (
        <ToolHeader
          spaceName={stack?.name || 'Stack'}
          toolName={getToolName()}
          currentTool="bookings"
          spaceTools={[]}
          parentBreadcrumb={getParentBreadcrumb()}
          isStackView={true}
          stackId={stackId}
        />
      )}

      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PageTransition>
            {/* Back Button */}
            <div className="pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackClick}
                className="mb-2"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Bookings
              </Button>
            </div>

            {/* Booking Header */}
            <div className="pt-4">
              <BookingHeader
                booking={booking}
                bookedOption={bookedOption}
                trainingProvider={trainingProvider}
                delegateCount={delegateCount}
                saving={saving}
                lastSaved={lastSaved}
                onSubmit={handleSubmitRequest}
                submitting={submitting}
                canSubmit={!missingProviders}
              />
            </div>

            {/* Tabbed Content Section */}
            <div className="py-8">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                {/* Folder tabs */}
                <div>
                  {/* Tabs row with bottom separator line */}
                  <div className="relative flex items-stretch" style={{ marginBottom: '-1px' }}>
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.value;
                      // Check if providers tab needs attention
                      const needsAttention = tab.value === 'providers' && missingProviders;
                      // Use warning icon when attention needed, otherwise use tab's icon
                      const DisplayIcon = needsAttention ? AlertTriangle : Icon;

                      return (
                        <button
                          key={tab.value}
                          onClick={() => setActiveTab(tab.value)}
                          className={`
                            flex items-center gap-1.5 text-[13px] cursor-pointer transition-colors
                            ${needsAttention
                              ? 'text-amber-600 font-medium'
                              : isActive
                                ? 'font-semibold text-gray-900'
                                : 'font-medium text-gray-500 hover:text-gray-700'
                            }
                          `}
                          style={isActive ? {
                            background: 'white',
                            borderTop: '1px solid #e5e7eb',
                            borderLeft: '1px solid #e5e7eb',
                            borderRight: '1px solid #e5e7eb',
                            borderBottom: '1px solid white',
                            borderRadius: '8px 8px 0 0',
                            padding: '10px 20px',
                            position: 'relative',
                            zIndex: 2,
                          } : {
                            background: 'transparent',
                            border: '1px solid transparent',
                            borderBottom: 'none',
                            padding: '10px 20px',
                            position: 'relative',
                            zIndex: 0,
                          }}
                        >
                          <DisplayIcon className="h-3.5 w-3.5" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Content - the folder body */}
                  <div className="bg-white border border-gray-200 rounded-b-lg p-6" style={{ borderTopRightRadius: '8px' }}>
                    {/* Overview Tab */}
                    <TabsContent value="overview" className="mt-0 p-0">
                      <OverviewContent
                        booking={booking}
                        onRefresh={fetchBookingDetails}
                        onSaveStateChange={handleSaveStateChange}
                      />
                    </TabsContent>

                    {/* Delegates Tab */}
                    <TabsContent value="delegates" className="mt-0 p-0">
                      <DelegatesContent
                        booking={booking}
                        onDelegateChange={fetchDelegateCount}
                        onSaveStateChange={handleSaveStateChange}
                      />
                    </TabsContent>

                    {/* Training Providers Tab - Draft only */}
                    <TabsContent value="providers" className="mt-0 p-0">
                      <TrainingProvidersContent
                        booking={booking}
                        onRefresh={fetchBookingDetails}
                        onSaveStateChange={handleSaveStateChange}
                      />
                    </TabsContent>

                    {/* Quote Options Tab - Live only */}
                    <TabsContent value="options" className="mt-0 p-0">
                      <QuoteOptionsContent
                        booking={booking}
                        onRefresh={fetchBookingDetails}
                      />
                    </TabsContent>

                    {/* Documents Tab */}
                    <TabsContent value="documents" className="mt-0 p-0">
                      <DocumentsContent
                        booking={booking}
                      />
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          </PageTransition>
        </div>
      </div>
    </AppShell3>
  );
};

export default ViewBookingPage;
